import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";

import { Pagination } from "../../../../shared/ui/common/Pagination";
import { getAvatarColor } from "../../../../shared/utils/avatar";
import { useCenter } from "../../../center";
import { GroupPatient, groupLabel, MIN_GROUP_SIZE } from "../../domain/GroupPatient";
import { useDeleteGroupPatient, useGroupPatients } from "../../application/useGroupPatients";
import { PatientOwnershipBadge } from "./PatientOwnership";

interface Props {
    filters?: { name: string };
    pageSize?: number;
}

const matches = (group: GroupPatient, needle: string) =>
    groupLabel(group).toLowerCase().includes(needle) ||
    group.members.some((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(needle));

/** The therapist's groups: name, who is in it, and whose it is inside a centre. */
export default function GroupsList({ filters, pageSize = 10 }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { center } = useCenter();
    const { groups, loading, failed } = useGroupPatients();
    const remove = useDeleteGroupPatient();
    const [offset, setOffset] = useState(0);

    const needle = filters?.name?.toLowerCase() ?? "";
    const shown = needle ? groups.filter((group) => matches(group, needle)) : groups;
    const start = offset < shown.length ? offset : 0;

    const handleDelete = async (group: GroupPatient) => {
        if (!confirm(t("patient.group.deleteConfirm"))) return;
        try {
            await remove.mutateAsync(group.id);
        } catch {
            alert(t("patient.errorDeleting"));
        }
    };

    if (loading) {
        return <div className="flex justify-center py-10 text-content-tertiary">{t("patient.loading")}</div>;
    }
    if (failed) {
        return <div className="p-4 text-center text-red-500 dark:text-red-400">{t("patient.errorLoading")}</div>;
    }
    if (!shown.length) {
        return (
            <div className="p-4 text-center text-content-tertiary">
                {groups.length ? t("patient.group.noMatches") : t("patient.group.empty")}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <ul className="grid grid-cols-1 gap-4">
                {shown.slice(start, start + pageSize).map((group) => (
                    <li
                        key={group.id}
                        className="group relative flex flex-col items-center justify-between rounded-2xl border border-gray-100 bg-white p-4 shadow-theme-xs transition-all duration-300 hover:border-brand-500/50 hover:shadow-raised dark:border-gray-800 dark:bg-gray-900/40 sm:flex-row"
                    >
                        <div className="flex w-full flex-1 items-center gap-4">
                            <div
                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${getAvatarColor(group.id)}`}
                            >
                                <GroupOfPeopleIcon className="h-6 w-6" aria-hidden="true" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/group-patient/${group.id}`)}
                                    className="block max-w-full truncate text-left text-base font-semibold text-content-primary transition-colors hover:text-brand-500"
                                >
                                    {groupLabel(group)}
                                </button>
                                <p className="mt-1 truncate text-sm text-content-tertiary">
                                    {t("patient.group.membersCount", { count: group.members.length })}
                                    {" · "}
                                    {group.members.map((m) => m.first_name).join(", ")}
                                </p>
                                {group.below_minimum && (
                                    <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                                        {t("patient.group.belowMinimum", { min: MIN_GROUP_SIZE })}
                                    </p>
                                )}
                            </div>
                        </div>

                        {center && (
                            <PatientOwnershipBadge
                                heldByCenter={group.held_by_center}
                                className="mt-3 sm:ml-4 sm:mt-0"
                            />
                        )}

                        <div className="ml-auto mt-4 flex items-center gap-2 sm:ml-4 sm:mt-0">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-xl bg-gray-50 p-2.5 text-gray-500 transition-all hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400"
                                onClick={() => navigate(`/group-patient/${group.id}`)}
                                aria-label={`${t("common.edit")}: ${groupLabel(group)}`}
                            >
                                <Pencil className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-xl bg-gray-50 p-2.5 text-gray-500 transition-all hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                                onClick={() => handleDelete(group)}
                                disabled={remove.isPending && remove.variables === group.id}
                                aria-label={`${t("patient.group.delete")}: ${groupLabel(group)}`}
                            >
                                <Trash2 className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            <Pagination
                offset={start}
                total={shown.length}
                pageSize={pageSize}
                onPrev={() => setOffset(Math.max(start - pageSize, 0))}
                onNext={() => setOffset(start + pageSize)}
            />
        </div>
    );
}
